import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { sortVariants } from '../lib/sortVariants';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import api from '../lib/api';
import MultiSelectFilter from './MultiSelectFilter';
import { useBulkSelect } from '../hooks/useBulkSelect';
import BulkActionBar from './BulkActionBar';

const RETURN_REASONS = [
  { value: 'defective', label: 'Defective' },
  { value: 'wrong_item', label: 'Wrong Item' },
  { value: 'excess', label: 'Excess Quantity' },
  { value: 'damaged', label: 'Damaged in Transit' },
  { value: 'quality_issue', label: 'Quality Issue' },
  { value: 'not_moving', label: 'Not Moving' },
  { value: 'other', label: 'Other' },
];

const PurchaseReturnManagement = ({ user }) => {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const bulk = useBulkSelect();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingReturn, setViewingReturn] = useState(null);
  const [expandedPR, setExpandedPR] = useState(null);
  const [searchPR, setSearchPR] = useState('');
  const [filterVendor, setFilterVendor] = useState('');
  const [filterStates, setFilterStates] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStaff, setFilterStaff] = useState([]);
  const [billingDialog, setBillingDialog] = useState(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const canCreate = ['super_admin', 'admin', 'manager', 'inventory_staff'].includes(user?.role);
  const canEdit = ['super_admin', 'admin', 'manager'].includes(user?.role);
  const canChangeStatus = ['super_admin', 'admin', 'manager', 'inventory_staff'].includes(user?.role);

  const updateTallyStatus = async (returnId, newStatus, debitNoteNumber) => {
    try {
      const payload = { tally_status: newStatus };
      if (debitNoteNumber) payload.debit_note_number = debitNoteNumber;
      await api.put(`/purchase-returns/${returnId}/tally-status`, payload);
      toast.success(`Status updated to ${newStatus === 'billed' ? 'Billed' : 'Pending'}`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
  };

  const handleStatusChange = (returnId, newStatus) => {
    if (newStatus === 'billed') {
      setBillingDialog({ entryId: returnId, inputValue: '' });
    } else {
      updateTallyStatus(returnId, newStatus);
    }
  };

  const confirmBilling = () => {
    if (!billingDialog) return;
    updateTallyStatus(billingDialog.entryId, 'billed', billingDialog.inputValue || null);
    setBillingDialog(null);
  };

  const loadData = async () => {
    try {
      const [returnsRes, vendorsRes] = await Promise.allSettled([
        api.get('/purchase-returns'), api.get('/vendors'),
      ]);
      if (returnsRes.status === 'fulfilled') setReturns(returnsRes.value.data);
      if (vendorsRes.status === 'fulfilled') setVendors(vendorsRes.value.data);
    } catch (err) { toast.error('Failed to load data'); }
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const vendorMap = Object.fromEntries(vendors.map(v => [v.id, v]));
  const getVendorDisplay = (id) => {
    const v = vendorMap[id];
    if (!v) return id;
    const name = v.name || v.company_name || id;
    const loc = v.city?.toLowerCase() === 'mumbai' ? v.area : v.city;
    return loc ? `${name} (${loc})` : name;
  };

  const filteredReturns = useMemo(() => {
    return returns.filter(r => {
      if (searchPR && !(vendorMap[r.vendor_id]?.name?.toLowerCase().includes(searchPR.toLowerCase())) &&
          !(/\d/.test(searchPR) && (r.return_number?.toLowerCase().includes(searchPR.toLowerCase()) ||
          r.dn_number?.toLowerCase().includes(searchPR.toLowerCase()))) &&
          !(r.items || []).some(item => item.design_code?.toLowerCase().includes(searchPR.toLowerCase()))) return false;
      if (filterVendor && r.vendor_id !== filterVendor) return false;
      if (filterStates.length && !filterStates.includes(vendorMap[r.vendor_id]?.state)) return false;
      if (filterCities.length && !filterCities.includes(vendorMap[r.vendor_id]?.city)) return false;
      if (filterDateFrom && r.return_date < filterDateFrom) return false;
      if (filterDateTo && r.return_date > filterDateTo) return false;
      if (filterStaff.length && !filterStaff.includes(r.created_by_name)) return false;
      return true;
    });
  }, [returns, searchPR, filterVendor, filterStates, filterCities, filterDateFrom, filterDateTo, filterStaff, vendorMap]);

  const stateFilterOptions = [...new Set(vendors.map(v => v.state).filter(Boolean))].sort().map(s => ({ value: s, label: s }));
  const cityFilterOptions = [...new Set(vendors.filter(v => !filterStates.length || filterStates.includes(v.state)).map(v => v.city).filter(Boolean))].sort().map(c => ({ value: c, label: c }));
  const staffFilterOptions = [...new Set(returns.map(r => r.created_by_name).filter(Boolean))].sort().map(n => ({ value: n, label: n }));
  const hasActiveFilters = !!(searchPR || filterVendor || filterStates.length || filterCities.length || filterDateFrom || filterDateTo || filterStaff.length);

  const deleteReturn = async (id, num) => {
    try {
      await api.delete(`/purchase-returns/${id}`);
      toast.success(`${num} deleted. Stock restored.`);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getFullYear()).slice(-2)}`;
  };
  const reasonLabel = (r) => RETURN_REASONS.find(x => x.value === r)?.label || r;
  const reasonColor = (r) => {
    if (r === 'defective' || r === 'damaged') return 'text-red-700 bg-red-50';
    if (r === 'quality_issue') return 'text-orange-700 bg-orange-50';
    if (r === 'excess') return 'text-blue-700 bg-blue-50';
    if (r === 'wrong_item') return 'text-purple-700 bg-purple-50';
    if (r === 'not_moving') return 'text-amber-700 bg-amber-50';
    return 'text-gray-700 bg-gray-50';
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" /></div>;

  return (
    <div className="p-6 space-y-6 animate-fadeIn" data-testid="purchase-return-container">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Nunito' }}>Purchase Returns</h1>
          <p className="text-gray-600 mt-1">Return goods to vendors, track debit notes</p>
        </div>
      </div>

      {/* Filters */}
      <Card data-testid="pr-filters">
        <CardContent className="p-4">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Search Return # / DN / Vendor</Label>
              <Input value={searchPR} onChange={e => setSearchPR(e.target.value)} placeholder="Return #, Debit Note, Vendor or Design No..." className="h-9" data-testid="pr-filter-search" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Vendor</Label>
              <select value={filterVendor} onChange={e => setFilterVendor(e.target.value)} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm" data-testid="pr-filter-vendor">
                <option value="">All Vendors</option>
                {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">State</Label>
              <MultiSelectFilter label="All States" options={stateFilterOptions} selected={filterStates} onChange={setFilterStates} testId="pr-filter-state" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">City</Label>
              <MultiSelectFilter label="All Cities" options={cityFilterOptions} selected={filterCities} onChange={setFilterCities} testId="pr-filter-city" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Date From</Label>
              <Input type="date" value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} className="h-9" data-testid="pr-filter-date-from" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-gray-600">Date To</Label>
              <Input type="date" value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} className="h-9" data-testid="pr-filter-date-to" />
            </div>
            <div className="space-y-1 min-w-[130px]">
              <Label className="text-xs font-semibold text-gray-600">Staff</Label>
              <MultiSelectFilter label="All Staff" options={staffFilterOptions} selected={filterStaff} onChange={setFilterStaff} testId="pr-filter-staff" />
            </div>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-9 text-xs text-red-500" onClick={() => { setSearchPR(''); setFilterVendor(''); setFilterStates([]); setFilterCities([]); setFilterDateFrom(''); setFilterDateTo(''); setFilterStaff([]); }} data-testid="pr-clear-filters">
                Clear All
              </Button>
            )}
          </div>
          {hasActiveFilters && <p className="text-xs text-gray-500 mt-2">Showing {filteredReturns.length} of {returns.length} returns</p>}
        </CardContent>
      </Card>

      <Card data-testid="pr-list">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <CardTitle>Purchase Returns ({filteredReturns.length})</CardTitle>
            {canCreate && (
              <Button size="sm" onClick={() => navigate('/create-purchase-return')} className="bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 shadow-sm" data-testid="create-pr-button">
                + New Return
              </Button>
            )}
          </div>
          <CardDescription>Goods returned to vendors with debit notes</CardDescription>
        </CardHeader>
        <CardContent>
          {filteredReturns.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{hasActiveFilters ? 'No matching returns' : 'No Returns Yet'}</h3>
              <p className="text-gray-600 mb-4">{hasActiveFilters ? 'Try adjusting your filters.' : 'Create a return when you need to send goods back to a vendor.'}</p>
              {!hasActiveFilters && canCreate && <Button onClick={() => navigate('/create-purchase-return')} className="bg-red-600">Create First Return</Button>}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="pr-table">
                <thead>
                  <tr className="bg-gray-50 border-b text-xs text-gray-500 uppercase tracking-wider">
                    <th className="px-2 py-3 w-10"><input type="checkbox" className="w-4 h-4 rounded" onChange={e => { if (e.target.checked) bulk.selectAll(filteredReturns.map(x => x.id)); else bulk.clearSelection(); }} /></th>
                    <th className="px-4 py-3 text-left font-medium">Return #</th>
                    <th className="px-4 py-3 text-left font-medium">Debit Note</th>
                    <th className="px-4 py-3 text-left font-medium">Date</th>
                    <th className="px-4 py-3 text-left font-medium">Vendor</th>
                    <th className="px-4 py-3 text-center font-medium">Items</th>
                    <th className="px-4 py-3 text-right font-medium">Qty</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3 text-left font-medium">Reasons</th>
                    <th className="px-4 py-3 text-left font-medium">By</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredReturns.map(ret => {
                    const reasons = new Set();
                    (ret.items || []).forEach(i => i.variants?.forEach(v => reasons.add(v.reason)));
                    return (
                      <tr key={ret.id} className={`hover:bg-red-50/50 cursor-pointer transition-colors group ${bulk.isSelected(ret.id) ? "bg-blue-50" : ""}`} data-testid={`pr-row-${ret.id}`} onClick={() => navigate(`/purchase-return/${ret.id}`)}>
                      <td className="px-2 py-3" onClick={e => e.stopPropagation()}><input type="checkbox" className="w-4 h-4 rounded" checked={bulk.isSelected(ret.id)} onChange={() => bulk.toggleSelect(ret.id)} /></td>
                        <td className="px-4 py-3">
                          <span className="font-bold text-red-700 font-mono group-hover:underline">{ret.return_number}</span>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-red-700" data-testid={`pr-dn-link-${ret.id}`}>{ret.debit_note_number || <span className="text-gray-400">-</span>}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatDate(ret.return_date)}
                          {ret.created_at && <span className="block text-[10px] text-gray-400">{new Date(ret.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 max-w-[200px] truncate">{getVendorDisplay(ret.vendor_id)}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{ret.items?.length || 0}</td>
                        <td className="px-4 py-3 text-right font-medium text-red-700">{ret.total_qty}</td>
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          {canChangeStatus ? (
                            <select
                              value={ret.tally_status || 'pending'}
                              onChange={e => handleStatusChange(ret.id, e.target.value)}
                              className={`text-xs font-medium px-2 py-1 rounded-md border cursor-pointer ${(ret.tally_status || 'pending') === 'billed' ? 'bg-green-100 text-green-700 border-green-300' : 'bg-yellow-100 text-yellow-700 border-yellow-300'}`}
                              data-testid={`pr-status-${ret.id}`}
                            >
                              <option value="pending">Pending</option>
                              <option value="billed">Billed</option>
                            </select>
                          ) : (
                            <Badge className={`text-xs ${(ret.tally_status || 'pending') === 'billed' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {(ret.tally_status || 'pending') === 'billed' ? 'Billed' : 'Pending'}
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {[...reasons].map(r => (
                              <span key={r} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${reasonColor(r)}`}>{reasonLabel(r)}</span>
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-500 truncate max-w-[100px]">{ret.created_by_name || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Debit Note Number Dialog */}
      <Dialog open={!!billingDialog} onOpenChange={v => { if (!v) setBillingDialog(null); }}>
        <DialogContent className="max-w-sm z-50">
          <DialogHeader>
            <DialogTitle>Enter Debit Note Number</DialogTitle>
            <DialogDescription>Optional: Enter the debit note number for this purchase return</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Debit Note Number</Label>
              <Input
                value={billingDialog?.inputValue || ''}
                onChange={e => setBillingDialog(prev => ({ ...prev, inputValue: e.target.value }))}
                placeholder="e.g. DN-001"
                autoFocus
                data-testid="billing-debit-note-input"
                onKeyDown={e => { if (e.key === 'Enter') confirmBilling(); }}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setBillingDialog(null)}>Cancel</Button>
              <Button className="bg-green-600 hover:bg-green-700" onClick={confirmBilling} data-testid="billing-confirm-btn">Mark as Billed</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <BulkActionBar selectedCount={bulk.selectedCount} selectedIds={bulk.selectedIds} docType="PR" label="purchase returns" onComplete={() => { bulk.clearSelection(); fetchReturns(); }} onClear={bulk.clearSelection} />
    </div>
  );
};

export default PurchaseReturnManagement;
